import type { PromoByProjectItem, PromoResolveResult, PromoBundleItem } from '@/api/promo'
import { daysLeftUntil } from '@/api/promo'
import {
    promoCanBeOffered,
    readAvailableActivationTypes,
    readPromoRelevance,
    resolveSmsOfferKind,
} from '@/helpers/promo/availableActivationTypes'

export { promoCanBeOffered, readAvailableActivationTypes, readPromoRelevance, resolveSmsOfferKind }
import type {
    MultichatActivePromo,
    MultichatPendingPromo,
} from '@/helpers/gcb2/multichat/types'
import {
    isDhChatIntegrationId,
    isMaxBusinessIntegrationId,
    isSmsChannelIntegrationId,
} from '@/helpers/promo/integrations'

function readIntegrationId(item: {
    payload?: { integrationId?: unknown } | null
    integrationId?: string | null
}): string | null {
    const fromPayload = item.payload?.integrationId
    if (fromPayload != null && String(fromPayload).trim()) {
        return String(fromPayload)
    }
    if (item.integrationId != null && String(item.integrationId).trim()) {
        return String(item.integrationId)
    }
    return null
}

function readDurationDays(payload: { durationDays?: unknown } | null | undefined): number | null {
    const durationRaw = payload?.durationDays
    if (typeof durationRaw === 'number') return durationRaw
    if (durationRaw != null) return Number(durationRaw) || null
    return null
}

function mapBundleItemToPending(item: PromoBundleItem): MultichatPendingPromo | null {
    if (item.activated) return null
    const key = String(item.key || '').trim()
    if (!key) return null
    const payload = item.payload || {}
    return {
        key,
        status: 'available',
        integrationId: readIntegrationId(item),
        durationDays: readDurationDays(payload),
        title: payload.title != null ? String(payload.title) : null,
    }
}

/**
 * Normalize resolve into pending (not yet activated) child promos.
 * Supports new `promos[]` bundle and legacy single-payload shape.
 */
export function extractPendingPromosFromResolve(
    data: PromoResolveResult,
    fallbackKey: string
): MultichatPendingPromo[] {
    if (data.status === 'not_found' || data.status === 'no_project') {
        return []
    }

    if (Array.isArray(data.promos) && data.promos.length > 0) {
        return data.promos
            .map(mapBundleItemToPending)
            .filter((item): item is MultichatPendingPromo => Boolean(item))
    }

    // Legacy: available + top-level payload
    if (data.status === 'available' || data.status === 'ok') {
        const payload = data.payload || {}
        const key = String(data.key || fallbackKey || '').trim()
        if (!key) return []
        return [
            {
                key,
                status: 'available',
                integrationId:
                    payload.integrationId != null ? String(payload.integrationId) : null,
                durationDays: readDurationDays(payload),
                title: payload.title != null ? String(payload.title) : null,
            },
        ]
    }

    return []
}

function hasInstall(item: PromoByProjectItem): boolean {
    return item.installId != null && String(item.installId).trim() !== ''
}

function isActivatedByProjectItem(item: PromoByProjectItem): boolean {
    if (item.lifecycle === 'offered' || item.lifecycle === 'activating') return false
    if (item.lifecycle === 'activated') return true
    const period = item.periodStatus
    if (period === 'active' || period === 'need_prolong' || period === 'expired') return true
    return hasInstall(item)
}

function mapByProjectPending(item: PromoByProjectItem): MultichatPendingPromo | null {
    if (item.lifecycle !== 'offered' && item.lifecycle !== 'activating') return null
    const key = String(item.key || '').trim()
    if (!key) return null
    const payload = item.payload || {}
    return {
        key,
        status: item.lifecycle === 'activating' ? 'activating' : 'available',
        integrationId: readIntegrationId(item),
        durationDays: readDurationDays(payload),
        title: payload.title != null ? String(payload.title) : null,
        activationType: item.activationType != null ? String(item.activationType) : null,
        availableActivationTypes: readAvailableActivationTypes(item.availableActivationTypes),
        relevance: readPromoRelevance(item.relevance),
    }
}

function mapByProjectActive(item: PromoByProjectItem): MultichatActivePromo | null {
    if (!isActivatedByProjectItem(item)) return null
    const integrationId = readIntegrationId(item)
    if (!integrationId) return null
    const periodStatus = item.periodStatus
    const status =
        periodStatus === 'need_prolong' || periodStatus === 'expired' ? periodStatus : 'active'
    const subscriptionRaw = item.subscriptionId
    const subscriptionId =
        typeof subscriptionRaw === 'number'
            ? subscriptionRaw
            : subscriptionRaw != null && subscriptionRaw !== ''
              ? Number(subscriptionRaw)
              : null
    return {
        promoId: item.promoId != null && String(item.promoId).trim() ? String(item.promoId) : '',
        key: String(item.key || '').trim(),
        integrationId,
        activeUntil: item.activeUntil != null ? String(item.activeUntil) : '',
        installId: item.installId != null ? String(item.installId) : '',
        subscriptionId:
            typeof subscriptionId === 'number' && Number.isFinite(subscriptionId)
                ? subscriptionId
                : null,
        daysLeft: daysLeftUntil(item.activeUntil),
        status,
        activationType: item.activationType != null ? String(item.activationType) : null,
        relevance: readPromoRelevance(item.relevance),
    }
}

/** Split GET /promo/byProject into pending (offered/activating) and activated. */
export function splitByProjectPromos(
    items: PromoByProjectItem[] | null | undefined
): {
    pendingPromos: MultichatPendingPromo[]
    activePromos: MultichatActivePromo[]
} {
    const list = Array.isArray(items) ? items : []
    const pendingPromos: MultichatPendingPromo[] = []
    const activePromos: MultichatActivePromo[] = []
    for (const item of list) {
        const pending = mapByProjectPending(item)
        if (pending) pendingPromos.push(pending)
        const active = mapByProjectActive(item)
        if (active) activePromos.push(active)
    }
    return { pendingPromos, activePromos }
}

/** GET api/promo/:key — read needProjectSelector from payload only. Do not use on resolve. */
export function resolveNeedsProjectSelector(data: {
    payload?: { needProjectSelector?: boolean } | null
} | null | undefined): boolean {
    return data?.payload?.needProjectSelector === true
}

const TOGGLE_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/
const FORBIDDEN_TOGGLE_KEYS = new Set(['__proto__', 'prototype', 'constructor'])

function normalizePromoLinkPath(path: unknown): string | null {
    if (typeof path !== 'string') return null
    const trimmed = path.trim()
    if (!trimmed) return null
    const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    if (withSlash.startsWith('//') || withSlash.includes('://')) return null
    return withSlash
}

function promoLinkMatches(
    link: { path?: unknown; keyValue?: unknown } | null | undefined,
    projectValue: unknown
): boolean {
    if (!link || typeof link !== 'object') return false
    if (typeof link.keyValue === 'boolean') return Boolean(projectValue) === link.keyValue
    return Object.is(projectValue, link.keyValue)
}

/**
 * SMS (and similar) promos: payload.toggleKey is a field on the project root.
 * Pick links[].path whose keyValue matches that field. `/` is the project home.
 * Legacy payload without toggleKey/links returns null — caller keeps redirectPath.
 */
export function pickPromoRedirectByToggle(
    payload: { toggleKey?: unknown; links?: unknown; [key: string]: unknown } | null | undefined,
    project: Record<string, unknown> | null | undefined
): string | null {
    const toggleKey = typeof payload?.toggleKey === 'string' ? payload.toggleKey.trim() : ''
    if (!toggleKey || !TOGGLE_KEY_RE.test(toggleKey) || FORBIDDEN_TOGGLE_KEYS.has(toggleKey)) {
        return null
    }
    const links = Array.isArray(payload?.links) ? payload.links : []
    if (links.length === 0) return null

    const projectValue = project != null ? project[toggleKey] : undefined
    const match = links.find(
        (link): link is { path?: unknown; keyValue?: unknown } =>
            Boolean(link) && typeof link === 'object' && promoLinkMatches(link, projectValue)
    )
    return match ? normalizePromoLinkPath(match.path) : null
}

export function findPendingDhChatPromo(
    pendingPromos: MultichatPendingPromo[] | null | undefined
): MultichatPendingPromo | null {
    if (!pendingPromos?.length) return null
    return (
        pendingPromos.find(
            (p) =>
                promoCanBeOffered(p) &&
                p.status === 'available' &&
                isDhChatIntegrationId(p.integrationId)
        ) || null
    )
}

export function findPendingMaxGiftPromo(
    pendingPromos: MultichatPendingPromo[] | null | undefined
): MultichatPendingPromo | null {
    if (!pendingPromos?.length) return null
    return (
        pendingPromos.find(
            (p) =>
                promoCanBeOffered(p) &&
                p.status === 'available' &&
                isMaxBusinessIntegrationId(p.integrationId)
        ) || null
    )
}

export type SmsPromoLinkView = 'sms_promo' | 'sms_crm_offer'

/** Unactivated SMS children of the promo link the user just followed. */
export function claimableSmsKeysFromResolve(
    data: PromoResolveResult,
    fallbackKey: string
): string[] {
    return extractPendingPromosFromResolve(data, fallbackKey)
        .filter((promo) => isSmsChannelIntegrationId(promo.integrationId))
        .map((promo) => promo.key)
}

/**
 * Promo-link entry into Multichat opens the SMS screen only while that
 * link's SMS gift is still offered (not activating, not already active).
 */
export function smsViewForOfferedPromoLink(args: {
    pendingPromos: MultichatPendingPromo[] | null | undefined
    smsExists: boolean
}): SmsPromoLinkView | null {
    const pending = (args.pendingPromos || []).find(
        (promo) =>
            promo.status === 'available' &&
            isSmsChannelIntegrationId(promo.integrationId) &&
            promoCanBeOffered(promo)
    )
    if (!pending) return null
    const kind = resolveSmsOfferKind(pending.availableActivationTypes, args.smsExists)
    if (kind === 'integration') return 'sms_promo'
    if (kind === 'discount') return 'sms_crm_offer'
    return null
}

export function findPendingSmsPromo(
    pendingPromos: MultichatPendingPromo[] | null | undefined
): MultichatPendingPromo | null {
    if (!pendingPromos?.length) return null
    return (
        pendingPromos.find(
            (p) =>
                promoCanBeOffered(p) &&
                (p.status === 'available' || p.status === 'activating') &&
                isSmsChannelIntegrationId(p.integrationId)
        ) || null
    )
}

/**
 * Single pending used by channel-flow helpers:
 * prefer dhChat (channel connect), else first available (gift-only).
 * SMS promo is rendered separately in the channels dock.
 */
export function pickPrimaryPendingPromo(
    pendingPromos: MultichatPendingPromo[]
): MultichatPendingPromo | null {
    return (
        findPendingDhChatPromo(pendingPromos) ||
        pendingPromos.find(
            (p) =>
                promoCanBeOffered(p) &&
                p.status === 'available' &&
                !isSmsChannelIntegrationId(p.integrationId)
        ) ||
        null
    )
}
